import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import './SurveyProfilerCategories.css';

const SurveyProfilerCategories = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categoryStats, setCategoryStats] = useState({});
  const [overallProgress, setOverallProgress] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);

  useEffect(() => {
    fetchCategoryStats();
  }, []);

  const fetchCategoryStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/qualifications/category-stats?t=${Date.now()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      setCategoryStats(response.data.categories);
      setTotalAnswered(response.data.totalAnswered);
      setTotalQuestions(response.data.totalQuestions);
      setOverallProgress(response.data.overallProgress);

      setLoading(false);
    } catch (err) {
      console.error('Failed to load category stats:', err);
      setLoading(false);
    }
  };

  const categories = [
    {
      id: 'personal_information',
      title: 'Personal Information',
      icon: '👤',
      description: 'Basic demographic information',
      image: '/assets/personal-info.png'
    },
    {
      id: 'family_information',
      title: 'Family Information',
      icon: '👨‍👩‍👧‍👦',
      description: 'Family structure and relationships',
      image: '/assets/family.png'
    },
    {
      id: 'employment_situation',
      title: 'Employment Situation',
      icon: '💼',
      description: 'Work and career details',
      image: '/assets/employment.png'
    },
    {
      id: 'financial_survey',
      title: 'Financial Survey',
      icon: '💰',
      description: 'Income and financial status',
      image: '/assets/financial.png'
    },
    {
      id: 'health_survey',
      title: 'Health Survey',
      icon: '🏥',
      description: 'Health and wellness information',
      image: '/assets/health.png'
    },
    {
      id: 'shopping_habits',
      title: 'Shopping Habits',
      icon: '🛒',
      description: 'Shopping preferences and behavior',
      image: '/assets/shopping.png'
    },
    {
      id: 'recreation_entertainment',
      title: 'Recreation and Entertainment',
      icon: '🎭',
      description: 'Hobbies and entertainment',
      image: '/assets/recreation.png'
    },
    {
      id: 'lifestyle',
      title: 'Lifestyle',
      icon: '🎯',
      description: 'Daily activities and preferences',
      image: '/assets/lifestyle.png'
    },
    {
      id: 'travel_survey',
      title: 'Travel Survey',
      icon: '✈️',
      description: 'Travel habits and preferences',
      image: '/assets/travel.png'
    }
  ];

  const handleCategoryClick = (categoryId) => {
    navigate(`/survey-profiler/${categoryId}`);
  };

  if (loading) {
    return (
      <div className="profiler-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading profile categories...</p>
        </div>
      </div>
    );
  }

  return (
      <div className="profiler-container">
          <div className="profiler-intro">
              <p className="intro-text">
                  Complete your personal profile to increase opportunities for receiving new studies
                  and <span className="highlight-text">earning abundant points</span>
              </p>
          </div>

          <div className="profiler-layout">
              {/* Left Column - Profile Completion (4 columns) */}
              <div className="profile-completion-column">
                  <div className="profile-completion-section">
                      <h2 className="section-title">Profile Completion Level</h2>

                      <div className="progress-circle-container">
                          <svg className="progress-ring" width="200" height="200">
                              <defs>
                                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                      <stop offset="0%" style={{ stopColor: '#7c3aed', stopOpacity: 1 }} />
                                      <stop offset="100%" style={{ stopColor: '#a78bfa', stopOpacity: 1 }} />
                                  </linearGradient>
                              </defs>
                              <circle
                                  className="progress-ring-bg"
                                  cx="100"
                                  cy="100"
                                  r="85"
                                  fill="none"
                                  stroke="#e2e8f0"
                                  strokeWidth="12"
                              />
                              <circle
                                  className="progress-ring-fill"
                                  cx="100"
                                  cy="100"
                                  r="85"
                                  fill="none"
                                  stroke="url(#progressGradient)"
                                  strokeWidth="12"
                                  strokeLinecap="round"
                                  style={{
                                      strokeDasharray: `${2 * Math.PI * 85}`,
                                      strokeDashoffset: `${2 * Math.PI * 85 * (1 - overallProgress / 100)}`,
                                      transition: 'stroke-dashoffset 0.5s ease'
                                  }}
                              />
                          </svg>
                          <div className="progress-percentage-overlay">
                              {Math.round(overallProgress)}%
                          </div>
                      </div>

                      <Link to="/survey-profiler/all" className="btn-complete-profile">
                          Complete personal profile
                      </Link>
                  </div>
              </div>

              {/* Right Column - Categories Grid (8 columns) */}
              <div className="categories-column">
                  <div className="categories-grid">
                      {categories
                          .filter((category) => {
                              const stats = categoryStats[category.id] || { answered: 0, total: 0, progress: 0 };
                              return stats.total > 0; // Only show categories with questions
                          })
                          .filter(category => category.id !== 'financial_survey')
                          .map((category) => {
                              const stats = categoryStats[category.id] || { answered: 0, total: 0, progress: 0 };
                              const POINTS_PER_QUESTION = 10;
                              const remainingQuestions = stats.total - stats.answered;
                              const totalPotentialPoints = stats.total * POINTS_PER_QUESTION;
                              const earnedPoints = stats.answered * POINTS_PER_QUESTION;
                              const remainingPoints = remainingQuestions * POINTS_PER_QUESTION;

                              return (
                                  <div
                                      key={category.id}
                                      className="category-card"
                                      onClick={() => handleCategoryClick(category.id)}
                                  >
                                      <div className="category-icon-wrapper">
                                          <div className="category-icon">{category.icon}</div>
                                          {stats.progress === 100 && (
                                              <div className="completion-badge">✓</div>
                                          )}
                                      </div>

                                  <div className="category-content">
                                    <h3 className="category-title">{category.title}</h3>

                                    {/* Reward Points Display */}
                                    <div className="category-points">
                                      {remainingPoints > 0 ? (
                                        <div className="points-remaining">
                                          <span className="points-icon">🎁</span>
                                          <span className="points-value">Earn {remainingPoints} points</span>
                                        </div>
                                      ) : (
                                          <div className="points-earned">
                                            <span className="points-icon">✨</span>
                                            <span className="points-value">{earnedPoints} points earned</span>
                                          </div>
                                      )}
                                    </div>

                                    <div className="category-progress">
                                      <div className="progress-bar-mini">
                                        <div
                                          className="progress-bar-fill-mini"
                                          style={{ width: `${stats.progress}%` }}
                                        />
                                      </div>
                                      <span className="progress-text">
                                        {stats.answered} / {stats.total} questions
                                      </span>
                                    </div>
                                      </div>
                                  </div>
                              );
                          })}
                  </div>
              </div>
      </div>
    </div>
  );
};

export default SurveyProfilerCategories;
